import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useFontSize } from './FontSizeContext';
import { useTranslation } from 'react-i18next';
import { Truck, Search, ChevronRight, RefreshCw, Box, Filter, X } from 'lucide-react';
import StatusBadge from './StatusBadge';

const ReceivingList = () => {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionOrg = session.org_id || '';
  const token = localStorage.getItem('token');
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [showAllSites, setShowAllSites] = useState(false);

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const fetchPendingPOs = useCallback(async (isAll = showAllSites) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/po/pending-receipts', {
        params: {
          all_sites: isAll ? true : false,
          org_id: sessionOrg
        },
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
      setPos(response.data);
    } catch (error) {
      console.error('Error fetching pending POs:', error);
    } finally {
      setLoading(false);
    }
  }, [showAllSites, sessionOrg, token]);

  useEffect(() => {
    fetchPendingPOs(showAllSites);
  }, [showAllSites, fetchPendingPOs]);

  useEffect(() => {
    const openAllSites = () => setShowAllSites(true);
    const viewMySite = () => setShowAllSites(false);
    window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
    window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
    return () => {
      window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
    };
  }, []);

  // 🎯 الداتا هنا أصلاً كل الـ POs المعلقة للاستلام بس (مش الـ POs كلها زي
  // POList.js)، مجموعة صغيرة نسبيًا فعليًا - بحث فوري على الكلاينت كفاية
  // ومحتاجناش تعقيد سيرفر-سايد pagination/sorting زي شاشة الـ PO الكاملة
  const filteredPos = useMemo(() => {
    if (!search.trim()) return pos;
    const q = search.trim().toLowerCase();
    return pos.filter(po =>
      po.ponum?.toLowerCase().includes(q) ||
      po.vendor?.toLowerCase().includes(q) ||
      po.description?.toLowerCase().includes(q)
    );
  }, [pos, search]);

  return (
    <div
      className={`flex flex-col h-full bg-surface-app text-[11px] uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left'}`}
      style={{ fontSize: `${fontSize}px` }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Toolbar */}
      <div className="bg-surface-card p-3 border-b shadow-sm z-20">
        <div className="flex justify-between items-center px-4">
          <h2 className="text-primary-dark font-black italic uppercase text-lg flex items-center gap-2">
            <Truck size={22} className="text-primary" />
            {t('receivingList.title', 'Receiving & Inspection')}
            <RefreshCw
              size={18}
              className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary hover:text-primary'} ${isRTL ? 'mr-2' : 'ml-2'}`}
              onClick={() => fetchPendingPOs()}
            />
          </h2>
          <div className="relative w-72">
            <Search size={14} className={`absolute top-1/2 -translate-y-1/2 text-ink-secondary ${isRTL ? 'right-3' : 'left-3'}`} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('receivingList.searchPlaceholder', 'Search PO, vendor, description...')}
              className={`w-full border border-line rounded-sm py-2 text-primary font-bold outline-none focus:border-primary bg-surface-app normal-case ${isRTL ? 'pr-8 text-right' : 'pl-8'}`}
            />
            {search && (
              <X size={14} className={`absolute top-1/2 -translate-y-1/2 text-ink-secondary hover:text-status-danger cursor-pointer ${isRTL ? 'left-3' : 'right-3'}`} onClick={() => setSearch('')} />
            )}
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
          <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
            <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className={`p-3 border-x border-white/10 ${isRTL ? 'border-l' : 'border-r'}`}>{t('po.tables.poNum', 'PO Number')}</th>
                <th className={`p-3 border-x border-white/10 ${isRTL ? 'border-l' : 'border-r'}`}>{t('receivingList.colVendor', 'Vendor')}</th>
                <th className={`p-3 border-x border-white/10 ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.desc', 'Description')}</th>
                <th className={`p-3 border-x border-white/10 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('assets.headers.status', 'Status')}</th>
                <th className={`p-3 border-x border-white/10 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('receivingList.colLines', 'Lines')}</th>
                <th className="p-3 text-center">{t('po.tables.orderDate', 'Order Date')}</th>
              </tr>
            </thead>
            <tbody className="divide-y font-bold">
              {loading ? (
                <tr><td colSpan="6" className="p-20 text-center text-ink-secondary font-black animate-pulse uppercase tracking-[0.2em]">{t('po.messages.syncing', 'Syncing...')}</td></tr>
              ) : filteredPos.length > 0 ? (
                filteredPos.map((po, i) => (
                  <tr key={i} className="hover:bg-primary-soft cursor-pointer transition-colors group" onClick={() => navigate(`/receiving/${po.ponum}`)}>
                    <td className={`p-3 border-x font-black text-primary group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>{po.ponum}</td>
                    <td className={`p-3 border-x text-ink font-bold ${isRTL ? 'border-l' : 'border-r'}`}>{po.vendor || '---'}</td>
                    <td className={`p-3 border-x text-ink-secondary italic normal-case ${isRTL ? 'border-l' : 'border-r'}`}>{po.description || '---'}</td>
                    <td className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                      <StatusBadge status={po.status} />
                    </td>
                    <td className={`p-3 border-x text-center text-ink-secondary font-mono ${isRTL ? 'border-l' : 'border-r'}`}>{po.total_lines ?? '---'}</td>
                    <td className="p-3 text-center text-ink-secondary font-bold">{po.orderdate || '---'}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('receivingList.noPending', 'No pending receipts')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-primary-dark text-white p-2 px-8 flex items-center gap-6 shadow-inner z-30 font-black uppercase tracking-widest text-[10px]">
        <div className="flex items-center gap-2">
          <Box size={14} className="text-primary" />
          <span>{t('common.total', 'Total')}: <span className="text-primary mx-1">{filteredPos.length}</span></span>
        </div>
        {search && (
          <div className={`flex items-center gap-2 border-white/20 px-6 ${isRTL ? 'border-r' : 'border-l'}`}>
            <Filter size={14} className="text-status-warning" />
            <span>{t('receivingList.filteredOf', 'Filtered from')}: <span className="text-status-warning mx-1">{pos.length}</span></span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceivingList;
